//imports
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { disableIdleLockSwitch, enableIdleLockSwitch, displayNotice, RootState, AppDispatch } from "../../../../globals";
import { getAccessPermissions } from "../../../../common";
import { HolidayCreatePayload, HolidayDTO, HolidayScheduleOptionDTO } from "../../../../models/HolidayDTO";
import { Allowed, HolidayForm } from "./interfaces";
import { horarioBonito } from "../../../../utils/horarioBonito";

//services
import HttpManager from "../../../../services/HttpManager";

//view
import AcademicCalendarView from "./academicCalendarView";

//messages
const noCampuses = "No hay planteles registrados o asignados al rol en uso por el usuario, por lo que el acceso a este módulo permanecerá deshabilitado.";
const initError = "La búsqueda inicial de datos no pudo ser completada.";
const saveError = "No se pudo guardar el asueto.";
const deleteError = "No se pudo borrar el asueto.";
const schedulesError = "No se pudieron cargar los horarios de esa fecha.";
const deleteConfirm = "Al borrar el asueto, las sesiones regresan a sus fechas.";

//constants
const SECTION_CODE = "academicCalendar";

/* Fecha local en YYYY-MM-DD (sin pasar por UTC, que corre el dia en la noche). */
const hoyISO = (): string => {
	const d = new Date();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${mm}-${dd}`;
};

const formularioVacio = (): HolidayForm => ({
	date: hoyISO(),
	name: "",
	typeId: 2,
	scope: "campus",
	scheduleId: null,
	notes: ""
});

const AcademicCalendarController = ({ embebido }: { embebido?: boolean } = {}) => {

	//init dispatch
	const dispatch = useDispatch<AppDispatch>();

	//init http
	const http = HttpManager.getInstance();

	//useSelector
	const sessionToken: string | null = useSelector((state: RootState) => state.sessionToken.value);
	const userProfile: any = useSelector((state: RootState) => state.userProfile.value);
	const idleLocked: boolean = useSelector((state: RootState) => state.idleLockSwitch.value);
	//El plantel es el que muestra el encabezado de WAPP (campusRef[currentUserCampus]);
	//la sesion v2 (sessionState) solo entra si ese aun no esta.
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const currentCampusId: number = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampusId);
	const currentCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);

	const campusHeader: any = campusRef && currentUserCampus != null ? campusRef[currentUserCampus] : null;
	const campusId: number = (campusHeader && campusHeader.id) || currentCampusId || 0;
	const campusName: string = (campusHeader && campusHeader.name) || (currentCampus && currentCampus.name) || "";

	const allowed: Allowed = useMemo(() => getAccessPermissions(userProfile, SECTION_CODE), [userProfile]);

	//useState
	const anioActual = new Date().getFullYear();

	const [sectionLoaded, setSectionLoaded] = useState<boolean>(false);
	const [extraYears, setExtraYears] = useState<number[]>([]);
	const [selectedYears, setSelectedYears] = useState<number[]>([anioActual, anioActual + 1, anioActual + 2]);
	const [search, setSearch] = useState<string>("");
	const [holidays, setHolidays] = useState<HolidayDTO[]>([]);
	const [form, setForm] = useState<HolidayForm>(formularioVacio());
	const [scheduleOptions, setScheduleOptions] = useState<HolidayScheduleOptionDTO[]>([]);
	const [loadingSchedules, setLoadingSchedules] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);

	/*
		Anios disponibles: anio pasado a tres adelante, mas los que traigan los
		asuetos cargados y los que se abran con "+ Año". Sin tope.
	*/
	const years: number[] = useMemo(() => {
		const base = [anioActual - 1, anioActual, anioActual + 1, anioActual + 2, anioActual + 3];
		const deAsuetos = holidays.map(h => Number(String(h.date).substring(0, 4))).filter(y => !isNaN(y) && y > 0);
		return Array.from(new Set([...base, ...deAsuetos, ...extraYears, ...selectedYears])).sort((a, b) => a - b);
	}, [anioActual, holidays, extraYears, selectedYears]);

	//functions
	const displayError = (message: string) => {
		dispatch(
			displayNotice({
				cornerClose: false,
				message: message,
				heading:
					<h3 style={{ color: "#A8730A", display: "inline-block" }}>
						Error
					</h3>
			})
		);
	};

	const getHolidays = async () => {
		if (!campusId || selectedYears.length === 0) {
			setHolidays([]);
			setSectionLoaded(true);
			return;
		}

		try {
			dispatch(enableIdleLockSwitch());

			const from = `${Math.min(...selectedYears)}-01-01`;
			const to = `${Math.max(...selectedYears)}-12-31`;
			const result = await http.academicCalendarService.getHolidays(campusId, from, to);

			if (result.status == 200 || result.status == 204) {
				const data: HolidayDTO[] = Array.isArray(result.data) ? result.data : [];
				setHolidays(data);
				setSectionLoaded(true);
			} else {
				displayError(initError);
			}
		} catch (error) {
			console.log(error);
			displayError(initError);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	const getSchedulesOnDate = async (date: string) => {
		if (!campusId || !date) {
			setScheduleOptions([]);
			return;
		}

		try {
			setLoadingSchedules(true);
			const result = await http.academicCalendarService.getSchedulesOnDate(campusId, date);
			const data: HolidayScheduleOptionDTO[] = Array.isArray(result.data) ? result.data : [];
			setScheduleOptions(data);

			//Si el horario elegido ya no da clase ese dia, se suelta.
			if (form.scheduleId != null && !data.some(s => s.id === form.scheduleId)) {
				setForm(prev => ({ ...prev, scheduleId: null }));
			}
		} catch (error) {
			console.log(error);
			setScheduleOptions([]);
			displayError(schedulesError);
		} finally {
			setLoadingSchedules(false);
		}
	};

	const onFormChange = (changes: Partial<HolidayForm>) => {
		setForm(prev => {
			const next = { ...prev, ...changes };
			if (next.scope === "campus") next.scheduleId = null;
			return next;
		});
	};

	const canSubmit: boolean = Boolean(
		form.date
		&& form.name.trim().length > 0
		&& (form.scope === "campus" || form.scheduleId != null)
	);

	const onSubmit = async () => {
		if (!canSubmit || !campusId || saving) return;

		const payload: HolidayCreatePayload = {
			date: form.date,
			name: form.name.trim(),
			typeId: form.typeId,
			scheduleId: form.scope === "schedule" ? form.scheduleId : null
		};
		const notes = form.notes.trim();
		if (notes) payload.notes = notes;

		try {
			setSaving(true);
			dispatch(enableIdleLockSwitch());
			const result = await http.academicCalendarService.createHoliday(campusId, payload);

			if (result.status >= 200 && result.status < 300) {
				const creado: HolidayDTO | null = result.data && typeof result.data === "object" ? result.data : null;
				const anio = Number(String(form.date).substring(0, 4));

				//Si el asueto cae fuera de los anios marcados, se abre ese anio para verlo
				//(el cambio de anios vuelve a pedir la lista).
				if (!isNaN(anio) && anio > 0 && !selectedYears.includes(anio)) {
					setSelectedYears(prev => [...prev, anio].sort((a, b) => a - b));
				} else if (creado && creado.id != null) {
					setHolidays(prev => [...prev.filter(h => h.id !== creado.id), creado]);
				} else {
					await getHolidays();
				}

				setForm(prev => ({ ...formularioVacio(), date: prev.date, scope: prev.scope, typeId: prev.typeId }));
			} else {
				displayError(saveError);
			}
		} catch (error: any) {
			console.log(error);
			const message: string | undefined = error && error.response && error.response.data && error.response.data.message;
			displayError(message || saveError);
		} finally {
			setSaving(false);
			dispatch(disableIdleLockSwitch());
		}
	};

	const deleteHoliday = async (holiday: HolidayDTO) => {
		if (!campusId) return;

		try {
			dispatch(enableIdleLockSwitch());
			const result = await http.academicCalendarService.deleteHoliday(campusId, holiday.id);

			if (result.status >= 200 && result.status < 300) {
				setHolidays(prev => prev.filter(h => h.id !== holiday.id));
			} else {
				displayError(deleteError);
			}
		} catch (error: any) {
			console.log(error);
			const message: string | undefined = error && error.response && error.response.data && error.response.data.message;
			displayError(message || deleteError);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	const onDelete = (holiday: HolidayDTO) => {
		dispatch(displayNotice({
			procedure: async () => { await deleteHoliday(holiday); },
			message: deleteConfirm,
			heading:
				<h3 style={{ color: "#2B1C16", display: "inline-block" }}>
					Borrar asueto
				</h3>
		}));
	};

	const onToggleYear = (year: number) => {
		setSelectedYears(prev =>
			prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year].sort((a, b) => a - b)
		);
	};

	/* "+ Año": agrega un anio mas adelante del ultimo visible y lo deja marcado. */
	const onAddYear = () => {
		const siguiente = Math.max(...years) + 1;
		setExtraYears(prev => prev.includes(siguiente) ? prev : [...prev, siguiente]);
		setSelectedYears(prev => prev.includes(siguiente) ? prev : [...prev, siguiente].sort((a, b) => a - b));
	};

	/* Filtro en el navegador: anios marcados (por si no son seguidos) y buscador. */
	const holidaysVisibles: HolidayDTO[] = useMemo(() => {
		const q = search.trim().toLowerCase();

		return holidays
			.filter(h => selectedYears.includes(Number(String(h.date).substring(0, 4))))
			.filter(h => {
				if (!q) return true;
				const texto = [h.name, h.scheduleName, horarioBonito(h.scheduleName), h.typeLabel, h.notes, h.date]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				return texto.includes(q);
			})
			.sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : a.id - b.id);
	}, [holidays, selectedYears, search]);

	//useEffect
	useEffect(() => {
		if (sessionToken) {
			campusId ? getHolidays() : displayError(noCampuses);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, selectedYears]);

	useEffect(() => {
		if (sessionToken && form.scope === "schedule") {
			getSchedulesOnDate(form.date);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.scope, form.date, campusId]);

	return (
		<AcademicCalendarView
			sectionLoaded={sectionLoaded}
			campusName={campusName}
			idleLocked={idleLocked}
			allowed={allowed}
			years={years}
			selectedYears={selectedYears}
			onToggleYear={onToggleYear}
			onAddYear={onAddYear}
			search={search}
			onSearch={setSearch}
			holidays={holidaysVisibles}
			onDelete={onDelete}
			form={form}
			onFormChange={onFormChange}
			scheduleOptions={scheduleOptions}
			loadingSchedules={loadingSchedules}
			saving={saving}
			canSubmit={canSubmit}
			onSubmit={onSubmit}
			embebido={embebido}
		/>
	);
}

export default AcademicCalendarController
